import { Step, StepLabel, Stepper } from "@mui/material";
import type { ArtifactDto } from "../../services/orchestratorApi";
import type { WorkflowStatus, StepProgressState } from "../../hooks/useWorkflowStatus";
import { toUserMessage } from "../../utils/userErrorMessage";
import { getColorHexCodeByColorName as token } from "../../tokens";

interface Props {
  workflowId: string;
  status: WorkflowStatus[];
  completed?: boolean;
  failed?: boolean;
  /**
   * When `failed` is true, indicates the failure was specifically during
   * auth. Drives copy ("Sign-in failed" instead of "Workflow failed") and
   * controls whether the auto-close countdown footer renders on the error
   * callout.
   */
  authFailed?: boolean;
  /** Countdown to task-pane auto-close. `null` = no countdown active. */
  autoCloseSecondsLeft?: number | null;
  /** Cancel the auto-close timer (rendered as a "Keep open" link). */
  onCancelAutoClose?: () => void;
  /** Artifacts produced by a completed workflow (e.g. screenshots). */
  artifacts?: ArtifactDto[];
  /**
   * Live step progress from the substrate `wf_event` SignalR push (AC-3).
   * When provided, renders "Step N of M: <step_id>" above the stepper.
   * Null = no push data yet; falls back to the legacy stepper-only view.
   */
  stepProgress?: StepProgressState | null;
}

const WORKFLOW_STEPS = [
  "Parsing email",
  "Enriching from Epic",
  "Plan review",
  "Creating endorsement",
  "Creating activity",
  "Carrier handoff",
];

function getActiveStep(status: WorkflowStatus[]): number {
  if (status.length === 0) return 0;
  const lastStep = status[status.length - 1].step;
  const stepMap: Record<string, number> = {
    start: 0,
    parse_email: 0,
    enrich_epic: 1,
    plan_ready: 2,
    approved: 3,
    create_endorsement: 3,
    create_activity: 4,
    carrier_handoff: 5,
    completed: 6,
    error: -1,
    rejected: -1,
  };
  return stepMap[lastStep] ?? 0;
}

/**
 * Progress / completed / failed view. Outer chrome is the ora idiom
 * (heading + tile-shadow section); the actual stepper is MUI styled with
 * the Highstreet brand palette via sx props (sunrise active dot,
 * primary-success-700 done, primary-error-700 failed).
 */
export default function StepProgress({
  workflowId,
  status,
  completed = false,
  failed = false,
  authFailed = false,
  autoCloseSecondsLeft = null,
  onCancelAutoClose,
  artifacts = [],
  stepProgress = null,
}: Props) {
  const activeStep = completed ? WORKFLOW_STEPS.length : getActiveStep(status);
  const lastStatus = status[status.length - 1];
  const carrierHandoff = status.find((s) => s.step === "carrier_handoff");

  const heading = completed
    ? "Endorsement complete"
    : failed
      ? authFailed
        ? "Sign-in failed"
        : "Workflow failed"
      : "Processing";

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-display-xs text-primary-gray-light-900">
          {heading}
        </h1>
        {!completed && !failed && (
          <p className="text-sm text-primary-gray-light-600 pt-[0.438rem]">
            {lastStatus?.message ?? "Starting…"}
          </p>
        )}
      </div>

      {/* Stepper inside a tile section — only when not failed */}
      {!failed && (
        <section className="w-full flex flex-row see-possible-tile-shadow pb-4 px-4">
          <div className="w-full gap-[0.625rem] flex flex-col">
            <div className="w-full flex flex-row h-[2.375rem] justify-start text-primary-gray-light-900 text-xl border-b-[1px] !border-solid border-b-sunrise">
              <div className="mr-3 items-center flex">Steps</div>
            </div>
            {stepProgress && stepProgress.totalSteps > 0 && (
              <p
                className="text-sm text-primary-gray-light-700 pt-1"
                data-testid="step-progress-label"
              >
                {`Step ${stepProgress.stepIndex + 1} of ${stepProgress.totalSteps}: ${stepProgress.displayName ?? stepProgress.currentStepId}`}
              </p>
            )}
            <Stepper
              activeStep={activeStep}
              orientation="vertical"
              sx={{
                pt: 1,
                "& .MuiStepLabel-label": {
                  fontSize: "0.875rem",
                  color: token("primary-gray-light-600"),
                  "&.Mui-active": {
                    color: token("primary-gray-light-900"),
                    fontWeight: 700,
                  },
                  "&.Mui-completed": {
                    color: token("primary-success-700"),
                  },
                },
                "& .MuiStepIcon-root": {
                  color: token("primary-gray-light-300"),
                  "&.Mui-active": {
                    color: token("sunrise"),
                  },
                  "&.Mui-completed": {
                    color: token("primary-success-700"),
                  },
                },
                "& .MuiStepIcon-text": {
                  fill: token("primary-gray-light-900"),
                  fontWeight: 700,
                },
              }}
            >
              {WORKFLOW_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </div>
        </section>
      )}

      {/* Outcome callouts — coloured to match the ora alert palette */}
      {completed && (
        <Callout
          tone="success"
          title="Endorsement processed successfully"
          body="The endorsement has been created in EPIC and a follow-up activity has been assigned."
          footer={
            autoCloseSecondsLeft !== null && autoCloseSecondsLeft > 0 ? (
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-xs text-primary-success-700">
                  Closing in {autoCloseSecondsLeft}s.
                </span>
                {onCancelAutoClose && (
                  <button
                    type="button"
                    onClick={onCancelAutoClose}
                    className="lnk-gray text-xs font-semibold px-1.5 py-0.5"
                  >
                    Keep open
                  </button>
                )}
              </div>
            ) : null
          }
        />
      )}
      {failed && lastStatus && (
        <Callout
          tone="error"
          title={authFailed ? "Sign-in failed" : "Workflow failed"}
          body={toUserMessage(lastStatus.message)}
          footer={
            authFailed &&
            autoCloseSecondsLeft !== null &&
            autoCloseSecondsLeft > 0 ? (
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-xs text-primary-error-700">
                  Closing in {autoCloseSecondsLeft}s.
                </span>
                {onCancelAutoClose && (
                  <button
                    type="button"
                    onClick={onCancelAutoClose}
                    className="lnk-gray text-xs font-semibold px-1.5 py-0.5"
                  >
                    Keep open
                  </button>
                )}
              </div>
            ) : null
          }
        />
      )}
      {completed && carrierHandoff && (
        <Callout
          tone="info"
          title="Carrier portal ready"
          body="Navigate to the carrier portal to complete the change."
          link={{
            href: "#",
            label: "Open carrier portal",
          }}
        />
      )}
      {completed && artifacts[0]?.kind === "screenshot" && (
        <img
          src={artifacts[0].url}
          alt="Workflow screenshot"
          style={{ width: "100%", marginTop: 8 }}
        />
      )}

      <div className="text-2xs text-primary-gray-light-500">
        Workflow ID: <code>{workflowId || "—"}</code>
      </div>
    </div>
  );
}

interface CalloutProps {
  tone: "success" | "error" | "info";
  title: string;
  body: string;
  link?: { href: string; label: string };
  footer?: React.ReactNode;
}

function Callout({ tone, title, body, link, footer }: CalloutProps) {
  const palette: Record<CalloutProps["tone"], string> = {
    success:
      "border-primary-success-200 bg-primary-success-50 text-primary-success-700",
    error:
      "border-primary-error-200 bg-primary-error-50 text-primary-error-700",
    info: "border-primary-warning-200 bg-primary-warning-50 text-primary-warning-700",
  };
  return (
    <div
      className={`rounded-md border px-3 py-2 flex flex-col gap-1 ${palette[tone]}`}
    >
      <div className="text-sm font-bold">{title}</div>
      <div className="text-sm">{body}</div>
      {link && (
        <a
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-semibold underline mt-1 self-start"
        >
          {link.label}
        </a>
      )}
      {footer}
    </div>
  );
}
